/**
 * Typed wrapper for the agent's read-only status endpoints.
 *
 * Routes (see apps/agent/src/dev-server.ts):
 *   GET  /healthz          → { ok: true }
 *   GET  /state            → AgentSnapshot ({ tankId, ts, apex })
 *
 * All control/scenario/override routes were removed in the monitor-only
 * conversion — this API can only observe, never write.
 *
 * Every call returns a discriminated `Result<T>` rather than throwing so
 * UI components can render network-loss states without try/catch noise.
 */
import { agentFetch } from './agent-fetch';
import { loadPublicEnv } from './env';
import type { AgentSnapshot } from './types';

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

function baseUrl(): string {
  return loadPublicEnv().agentDevUrl.replace(/\/+$/, '');
}

async function request<T>(path: string, init?: RequestInit): Promise<Result<T>> {
  try {
    const res = await agentFetch(`${baseUrl()}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) {
      return { ok: false, error: `HTTP ${res.status} ${res.statusText}` };
    }
    const value = (await res.json()) as T;
    return { ok: true, value };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

export const devApi = {
  healthz(): Promise<Result<{ ok: boolean }>> {
    return request<{ ok: boolean }>('/healthz');
  },
  state(): Promise<Result<AgentSnapshot>> {
    return request<AgentSnapshot>('/state');
  },
};

export type DevApi = typeof devApi;
